import { Alert, AlertTitle, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { request } from '../../../interfaces/networkManager';
import {
	DataGrid,
	GridActionsCellItem,
	GridColDef
} from '@mui/x-data-grid';
import { frFR } from '@mui/x-data-grid/locales';
import DeleteIcon from '@mui/icons-material/Delete';
import { getCurrentUrl } from '../../../services/ApiServices';
import { headers } from '../../../services/genericConstantes';
import { Connexion } from '../../../models/connexion';
import { ToastContainer, toast } from 'react-toastify';
import { UserDTO } from '../../../models/userDTO';
import Error from '../../../components/Error/Error';
import { Application } from '../../../models/application';

type Props = {
	user: UserDTO;
};

const UserConnections: React.FC<Props> = ({ user }) => {
	const [connexions, setConnexions] = useState<Connexion[]>([]);
	const [connexionsFiltered, setConnexionsFiltered] = useState<Connexion[]>([]);
	const [filter, setIsFilter] = useState({ isFilter: false, applicationFilter: '' });
	const [error, setError] = useState<string | null>(null);
	const [applications, setApplications] = useState<Application[]>([]);
	const [openDialogDelete, setOpenDialogDelete] = useState(false);
	const columns: GridColDef[] = [
		{
			headerName: 'ID Appareil',
			field: 'uniqueId',
			type: 'string',
			align: 'center',
			headerAlign: 'center',
			flex: 1,
			valueGetter: (value, row) => row.device.uniqueId,
		},
		{
			headerName: "Système d'exploitation",
			field: 'operatingSystem',
			type: 'string',
			align: 'center',
			headerAlign: 'center',
			flex: 1,
			valueGetter: (value, row) => row.device.operatingSystem,
		},
		{
			headerName: 'Marque',
			field: 'brand',
			type: 'string',
			align: 'center',
			headerAlign: 'center',
			flex: 1,
			valueGetter: (value, row) => row.device.brand,
		},
		{
			headerName: 'Application',
			field: 'name',
			type: 'string',
			align: 'center',
			headerAlign: 'center',
			flex: 1,
			valueGetter: (value, row) => row.application.name,
		},
		{
			headerName: 'Version de l\'application',
			field: 'applicationVersion',
			type: 'string',
			align: 'center',
			headerAlign: 'center',
			flex: 1,
			valueGetter: (value, row) => row.applicationVersion,
		},
		{
			headerName: 'Dernière connexion',
			field: 'dateLastConnection',
			type: 'string',
			align: 'center',
			headerAlign: 'center',
			flex: 1,
			valueGetter: (value, row) => {
				const date = new Date(row.dateLastConnection);
				return date.toLocaleDateString('fr-FR');
			},
		},
		{
			field: 'actions',
			type: 'actions',
			flex: 1,
			getActions: (params: { row: Connexion }) => [
				<GridActionsCellItem
					key="delete"
					icon={<DeleteIcon />}
					label="Supprimer la connexion"
					onClick={() => deleteConnection(params.row)}
				/>,
			],
		},
	];

	const refreshData = React.useCallback(async () => {
		setConnexions([]);
		setConnexionsFiltered([]);
		setApplications([]);
		setIsFilter({ isFilter: false, applicationFilter: '' });

		const postOptions = {
			method: 'GET',
		};
		const applications: any = await request('/applications/', postOptions);
		const connections: any = await request(`/connections/${user.idPerson}`, postOptions);

		if (applications) {
			const dataApp = applications.map((application: any) => ({
				...application,
				currentDevice: 0,
			}));
			setApplications(dataApp);

			if (connections) {
				const data = connections.map((connexion: any) => ({
					...connexion,
					primarykey: `${connexion?.device?.id}-${connexion?.application?.id}-${connexion?.user?.idPerson}`,
				}));
				setConnexions(data);
				data.forEach((connexion: any) => {
					const application = dataApp.find(
						(app: Application) => app.id === connexion.application.id
					);
					if (application) {
						application.currentDevice += 1;
					}
				});
			} else {
				setError('Erreur lors de la récupération des connexions');
			}
		} else {
			setError('Erreur lors de la récupération des applications');
		}
	}, [user.idPerson, setConnexions, setConnexionsFiltered, setApplications, setIsFilter, setError]);

	useEffect(() => {
		refreshData();
	}, [refreshData]);

	const deleteButtonDisabled = () => {
		let disabled = false;
		if (filter.isFilter) {
			if (connexionsFiltered.length === 0) {
				disabled = true;
			}
		} else {
			if (connexions.length === 0) {
				disabled = true;
			}
		}
		return disabled;
	}

	const deleteConnection = (connexion: Connexion) => {
		const options = {
			headers: headers(),
			method: 'DELETE',
		};

		fetch(`${getCurrentUrl()}/connections/users/${connexion.user.idPerson}/applications/${connexion.application.id}/devices/${connexion.device.id}`, options)
			.then((response: any) => {
				if (response.ok) {
					toast.success('La connexion a bien été supprimée', {
						position: 'bottom-center',
						theme: 'colored',
					});
				}
				refreshData();
			})
			.catch((error: string) => {
				console.error(error);
				toast.error('Erreur lors de la suppression de la connexion', {
					position: 'bottom-center',
					theme: 'colored',
				});
			}
			);
	};

	const deleteAllConnections = (idUser: number) => {
		const options = {
			headers: headers(),
			method: 'DELETE',
		};

		fetch(`${getCurrentUrl()}/connections/users/${idUser}`, options)
			.then((response: any) => {
				if (response.ok) {
					toast.success('Les connexions ont bien été supprimées', {
						position: 'bottom-center',
						theme: 'colored',
					});
				}
				refreshData();
			})
			.catch((error: string) => {
				console.error(error);
				toast.error('Erreur lors de la suppression des connexions', {
					position: 'bottom-center',
					theme: 'colored',
				});
			}
			);
	};

	const deleteConnectionByApplication = (idUser: number, idApplication: number) => {
		const options = {
			headers: headers(),
			method: 'DELETE',
		};

		fetch(`${getCurrentUrl()}/connections/users/${idUser}/applications/${idApplication}`, options)
			.then((response: any) => {
				if (response.ok) {
					toast.success('Les connexions ont bien été supprimées', {
						position: 'bottom-center',
						theme: 'colored',
					});
				}
				refreshData();
			})
			.catch((error: string) => {
				console.error(error);
				toast.error('Erreur lors de la suppression des connexions', {
					position: 'bottom-center',
					theme: 'colored',
				});
			}
			);
	}

	const handleDeleteConnections = (filter: boolean) => {
		if (filter) {
			const idApplication = connexionsFiltered[0]?.application.id;
			deleteConnectionByApplication(user.idPerson, idApplication);
		} else {
			deleteAllConnections(user.idPerson);
		}

		setOpenDialogDelete(false);
	}

	const handleFilter = (applicationName: string) => {
		if (filter.isFilter && applicationName === filter.applicationFilter) {
			setConnexionsFiltered([]);
			setIsFilter({ isFilter: false, applicationFilter: '' });
		} else {
			const filteredConnexions = connexions.filter(
				(connexion: Connexion) => connexion.application.name === applicationName
			);
			setConnexionsFiltered(filteredConnexions);
			setIsFilter({ isFilter: true, applicationFilter: applicationName });

		}

	};

	return (
		<>
			<Typography variant="h6">Appareils connectés :</Typography>
			<Grid container spacing={1.5} sx={{ mt: 0.5 }}>
				{error && <Error message={error} />}
				{connexions.length === 0 && (
					<Grid size={{ xs: 12, lg: 12 }}>
						<Alert severity="info">
							<AlertTitle>Information</AlertTitle>
							Aucune connexion enregistré pour cet utilisateur
						</Alert>
					</Grid>
				)}
				{applications.map((application: Application) => (
					<Grid size={{ xs: 12, lg: 3 }} key={application.id} onClick={() => handleFilter(application.name)} sx={{ cursor: 'pointer' }}>
						<Alert
							severity={user.typeUser === "technician" || user.typeUser === "admin" ? (application.currentDevice >= application.nbDeviceMaxPHF ? 'error' : 'success') : (application.currentDevice >= application.nbDeviceMaxBreeder ? 'error' : 'success')}
							style={{
								backgroundColor: user.typeUser === "technician" || user.typeUser === "admin" ?
									(application.currentDevice >= application.nbDeviceMaxPHF ?
										(filter.applicationFilter === application.name ? 'rgba(253 161 161)' : 'rgba(253, 237, 237)') : (filter.applicationFilter === application.name ? 'rgba(173 239 173)' : 'rgba(237, 247, 237)')) :
									(application.currentDevice >= application.nbDeviceMaxBreeder ?
										(filter.applicationFilter === application.name ? 'rgba(253 161 161)' : 'rgba(253, 237, 237)') : (filter.applicationFilter === application.name ? 'rgba(173 239 173)' : 'rgba(237, 247, 237)')
									)
							}}
						>
							<AlertTitle>{application.name}</AlertTitle>
							{`${application.currentDevice} / ${user.typeUser === "technician" || user.typeUser === "admin" ? application.nbDeviceMaxPHF : application.nbDeviceMaxBreeder} appareils connectés`}
						</Alert>
					</Grid>
				))}
				<Grid size={{ xs: 12, lg: 12 }} sx={{ textAlign: 'right' }}>
					<Button variant="contained" color="error" disabled={deleteButtonDisabled()} onClick={() => setOpenDialogDelete(true)}>Supprimer les connexions</Button>
				</Grid>
				<Grid size={{ xs: 12, lg: 12 }}>
					<DataGrid
						localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
						rows={filter.isFilter ? connexionsFiltered : connexions}
						columns={columns}
						getRowId={(row) => row.primarykey}
						disableRowSelectionOnClick
					/>
				</Grid>
			</Grid>
			<ToastContainer />
			<Dialog open={openDialogDelete} onClose={() => setOpenDialogDelete(false)}>
				<DialogTitle>Suppression des connexions</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{filter.isFilter ?
							`Voulez-vous supprimer les connexions de l'application ${filter.applicationFilter} ?` :
							`Voulez-vous supprimer toutes les connexions des applications ?`
						}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button variant="contained" color="error" onClick={() => setOpenDialogDelete(false)}>
						Annuler
					</Button>
					<Button variant="contained" color="success" onClick={() => handleDeleteConnections(filter.isFilter)} autoFocus>
						Confirmer
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default UserConnections;
